import { useEffect, useRef, type ReactNode } from "react";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";

/**
 * Full-height scroll container for the public marketing pages (landing page,
 * privacy policy) with an overlay scrollbar:
 *
 *  - the native scrollbar is hidden, so content spans the full viewport width
 *    and the thumb floats over it instead of taking space;
 *  - only the thumb is drawn - no track background, no arrow buttons;
 *  - clicking the track does nothing: the page moves only by dragging the
 *    thumb, by wheel or touch scrolling, or by the keyboard;
 *  - the thumb rests at reduced opacity while idle and comes to full opacity
 *    while scrolling or when hovered (see .qb-scrollbar in landing.css).
 *
 * The viewport is focused on mount so keyboard scrolling works immediately.
 * With the window itself no longer scrollable, the browser would otherwise
 * have nothing to send arrow keys to until the user clicked somewhere.
 */
const MarketingScrollArea = ({ children }: { children: ReactNode }) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    viewportRef.current?.focus({ preventScroll: true });
  }, []);

  return (
    <ScrollAreaPrimitive.Root
      type="scroll"
      scrollHideDelay={800}
      className="relative h-screen overflow-hidden"
    >
      <ScrollAreaPrimitive.Viewport
        ref={viewportRef}
        tabIndex={-1}
        className="h-full w-full scroll-smooth outline-none"
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollAreaPrimitive.ScrollAreaScrollbar
        forceMount
        orientation="vertical"
        className="qb-scrollbar"
        onPointerDown={(event) => {
          // Radix treats a pointer-down anywhere on the scrollbar as the start
          // of a drag and jumps the thumb to the pointer. Block that unless the
          // pointer is actually on the thumb; preventDefault() makes Radix skip
          // its own handler.
          if (!thumbRef.current?.contains(event.target as Node)) {
            event.preventDefault();
          }
        }}
      >
        <ScrollAreaPrimitive.ScrollAreaThumb
          ref={thumbRef}
          className="qb-scrollbar-thumb"
        />
      </ScrollAreaPrimitive.ScrollAreaScrollbar>
    </ScrollAreaPrimitive.Root>
  );
};

export default MarketingScrollArea;
